import React from "react";

interface CategoryDepthCoverageDatum {
  depth: number;
  count: number;
}

interface CategoryDepthCoverageProps {
  data: CategoryDepthCoverageDatum[];
  t: (key: string) => string;
  locale: string;
}

export function CategoryDepthCoverage({
  data,
  t,
  locale,
}: CategoryDepthCoverageProps) {
  const maxCount = data.reduce((max, item) => Math.max(max, item.count), 0);

  if (!data.length || maxCount <= 0) {
    return <div className="text-sm text-secondary">{t("statistics.noData")}</div>;
  }

  const resolveDepthLabel = (depth: number) => {
    switch (depth) {
      case 0:
        return t("statistics.depthMeaning0");
      case 1:
        return t("statistics.depthMeaning1");
      case 2:
        return t("statistics.depthMeaning2");
      case 3:
        return t("statistics.depthMeaning3");
      case 4:
        return t("statistics.depthMeaning4");
      default:
        return t("statistics.depthMeaningFallback").replace(
          "{{depth}}",
          String(depth)
        );
    }
  };

  return (
    <div className="space-y-4">
      {data.map((item) => {
        const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
        return (
          <div key={`depth-${item.depth}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-primary">
                {resolveDepthLabel(item.depth)}
              </span>
              <span className="text-secondary">
                {item.count.toLocaleString(locale)}
              </span>
            </div>
            <div
              className="h-2 rounded-full"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--foreground) 10%, var(--background))",
              }}
            >
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.max(4, pct)}%`,
                  background:
                    "linear-gradient(90deg, var(--category-scale-2), var(--category-scale-4))",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
